import React from "react";
import { Link } from "react-router-dom";
import { Building2, Mail, ShieldCheck, Landmark, FileText, Send, Inbox as InboxIcon } from "lucide-react";

const EMAIL = "salvatorebungaro1@gmail.com";
const TITOLARE = "Capital Stone S.r.l. e le società del gruppo che utilizzano la piattaforma";

const FUNZIONI = [
  [Landmark, "Tesoreria e riconciliazione bancaria", "Saldi e movimenti dei conti aziendali acquisiti in sola lettura tramite Open Banking (PSD2) e riconciliati con la contabilità interna."],
  [FileText, "Operazioni immobiliari e budget", "Controllo di gestione per singola operazione: budget, costi, marginalità, unità immobiliari e rapporti con gli associati in partecipazione."],
  [InboxIcon, "Posta operativa", "Le email amministrative (fatture, preventivi, comunicazioni di fornitori e clienti) vengono lette e archiviate automaticamente e collegate all'operazione di riferimento."],
  [Send, "Risposte e invii dalla casella aziendale", "Su conferma dell'operatore, la piattaforma invia dalla casella aziendale richieste di documenti, solleciti e comunicazioni ai fornitori."],
];

const Card = ({ icon: Icon, titolo, testo, testid }) => (
  <div className="rounded-xl border bg-card p-5" data-testid={testid}>
    <Icon className="w-5 h-5 text-primary mb-3" />
    <h3 className="font-semibold mb-1">{titolo}</h3>
    <p className="text-sm leading-relaxed text-muted-foreground">{testo}</p>
  </div>
);

export default function Info() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="page-info">
      <div className="max-w-4xl mx-auto px-6 py-14 space-y-12">
        <header className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center"><Building2 className="w-6 h-6 text-primary" /></div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Capital Control</h1>
              <p className="text-sm text-muted-foreground">Real Estate Operations &amp; Investment Management</p>
            </div>
          </div>
          <p className="text-base leading-relaxed text-muted-foreground max-w-2xl">
            Capital Control è il software gestionale utilizzato da {TITOLARE} per governare le operazioni
            immobiliari: liquidità, associati in partecipazione, budget, fatture, cantieri e assistenza post-vendita.
            L'accesso è riservato al personale e ai collaboratori autorizzati.
          </p>
        </header>

        <section className="space-y-4">
          <h2 className="text-lg md:text-lg font-semibold">Cosa fa la piattaforma</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {FUNZIONI.map(([icon, t, d], i) => <Card key={t} icon={icon} titolo={t} testo={d} testid={`info-funzione-${i}`} />)}
          </div>
        </section>

        <section className="space-y-3 rounded-xl border bg-card p-6" data-testid="info-google-scopes">
          <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">Perché chiediamo l'accesso a Gmail</h2></div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Per la funzione "Posta operativa" la piattaforma si collega, con autorizzazione esplicita del titolare
            della casella, alle caselle Gmail aziendali del gruppo. L'accesso serve esclusivamente a:
          </p>
          <ul className="text-sm leading-relaxed text-muted-foreground space-y-2 list-disc pl-5">
            <li><strong>Lettura dei messaggi</strong> (<code>gmail.readonly</code>): individuare e archiviare fatture, preventivi e documenti ricevuti dai fornitori, collegandoli all'operazione immobiliare e al fornitore corretti.</li>
            <li><strong>Invio dei messaggi</strong> (<code>gmail.send</code>): inviare, su conferma dell'operatore, richieste di documenti, solleciti di pagamento e comunicazioni amministrative dalla casella aziendale.</li>
          </ul>
          <p className="text-sm leading-relaxed text-muted-foreground">
            I dati delle email sono utilizzati solo per queste finalità amministrative interne, non sono
            condivisi con terzi né usati per finalità pubblicitarie o di profilazione, e non vengono impiegati
            per addestrare modelli di intelligenza artificiale generici. Il collegamento può essere revocato in
            qualsiasi momento dall'utente, dalla piattaforma o dalle impostazioni dell'account Google.
          </p>
        </section>

        <section className="space-y-2" data-testid="info-contatti">
          <h2 className="text-lg font-semibold">Contatti</h2>
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a>
          </p>
          <p className="text-sm text-muted-foreground">Titolare: {TITOLARE} · Foro competente: Milano.</p>
        </section>

        <footer className="flex flex-wrap gap-4 text-sm pt-6 border-t">
          <Link to="/privacy" className="text-primary hover:underline" data-testid="info-link-privacy">Informativa privacy</Link>
          <Link to="/termini" className="text-primary hover:underline" data-testid="info-link-termini">Termini e condizioni</Link>
          <Link to="/login" className="text-muted-foreground hover:underline sm:ml-auto" data-testid="info-link-login">Accedi alla piattaforma</Link>
        </footer>
      </div>
    </div>
  );
}
